/**
 * 绘制单张大板
 */
import { translateLang } from '@/data/i18n/translateLang'
import { DrawBigPlankParams } from '@/data/plank'
import { bigPlankLockImage } from '@/data/plank'
import { IBigPlank, PointType, PointUpperCase } from '@/partTypes'
import store from '@/store'
import { translate as t } from '@/util/commonFun'
import { rectToPath, toDecimal } from '@/util/commonFuncs'

import { checkBigPlankIsSpecialShape } from '../common'
import {
  getEdgeLengthsFromLShape,
  getPathMinMaxPoint,
} from '../plankCommonFuncs'
import { toUpperCasePoint } from '../transform/graph'

export type MarkRectType = {
  x: number
  y: number
  width: number
  height: number
  type: 'surplus' | 'singleMark'
  bigPlank: IBigPlank
}

/** 顶部文字信息 */
type TopInfoSingle = {
  text: string
  lineHeight: number
  type?: BoxType
  gap?: number
  before?: TopInfoSingle
  after?: TopInfoSingle
}

/**
 * 规定大板单个部位绘制完成后的返回值类型,可用于后期重构画布点击激活以及相应等相关逻辑
 */
interface DrawBoxReturnType {
  /** 此盒子绘制的类型 */
  type: BoxType
  /** 大板绘制起点 x */
  plankStartX: number
  /** 大板绘制起点 y */
  plankStartY: number
  /** 绘制的路径,需要是页面显示路径，也就是加上了 startX startY*/
  path: PointType[]
  /** 当前大板 */
  plank: IBigPlank
}

/** 各个边界盒子的类型,在相应盒子触发相应事件时会返回此参数 */
enum BoxType {
  /** 顶部余料库名称 */
  'topInfo:surplusStoreName' = 'topInfo:surplusStoreName',
  /** 顶部余料圆圈标识 */
  'topInfo:surplusCircle' = 'topInfo:surplusCircle',
  /** 锁定图案 */
  'lock:lock' = 'lock:lock',
  /** 顶部单面板圆圈标识 */
  'topInfo:toggleFlag' = 'topInfo:toggleFlag',
}
/**
 * @description 一张大板的所有绘制逻辑，此类只关注绘制，如何绘制由外部决定
 * 除边框以外的其他绘制逻辑的起点均是按照边框的绘制起点来定的，和之前不同
 */
export class BigPlankDrawer {
  /** @description 是否为活跃大板 */
  isActive = false
  images = bigPlankLockImage
  startX?: number
  startY?: number
  width?: number
  height?: number
  /** 需要记录的标记rect */
  needMarkRectArr: {
    x: number
    y: number
    width: number
    height: number
    type: string
    bigPlank: IBigPlank
  }[] = []
  constructor(
    public bigPlank: IBigPlank,
    public ncSetting: any,
    public defaultScale: number,
    public scale: number
  ) {}
  /** @description 设置绘制起点，起点是基于大板边框的（特殊情况才会用到） */
  setStart(startX: number, startY: number) {
    this.startX = startX
    this.startY = startY
  }
  /** @description 获取起点 */
  private getStart() {
    return {
      x: this.startX ?? this.bigPlank.startX,
      y: this.startY ?? this.bigPlank.startY,
    }
  }
  /** @description 设置大板大小（特殊情况才会用到） */
  setSize(width: number, height: number) {
    this.width = width
    this.height = height
  }
  /** @description 获取大板大小 */
  private getSize() {
    const [_, __, width, height] = getPathMinMaxPoint(this.bigPlank.path ?? [])
    const scaleFunc = translateScaleByDraw(this.defaultScale, this.scale)
    return {
      width: this.width ?? scaleFunc(width),
      height: this.height ?? scaleFunc(height),
    }
  }
  /** @description 获取边框的绘制起点，为了后续的计算好处理一点 */
  getMarginStart() {
    const { x, y } = this.getStart()
    return {
      x,
      y: y + this.scaleFunc(DrawBigPlankParams.plankMarginY / 2),
    }
  }
  /** @description 获取大板绘制起点 */
  getContainerStart() {
    const { x, y } = this.getMarginStart()
    return {
      x: x + this.scaleFunc(DrawBigPlankParams.margin),
      y:
        y +
        this.scaleFunc(DrawBigPlankParams.toggleHeight) +
        this.scaleFunc(DrawBigPlankParams.margin),
    }
  }
  /** @description 绘制大板顶部信息 */
  drawTopInfo(ctx: CanvasRenderingContext2D) {
    const { x: startX, y: startY } = this.getMarginStart()
    const { topInfoTextColor, topInfoMargin, topInfoLineGap } =
      DrawBigPlankParams
    ctx.save()
    // 大板顶部所有文字基线均按照中心来处理，之后的处理中需要考虑文字是按照中心点绘制的，x 轴无影响
    ctx.textBaseline = 'middle'
    ctx.fillStyle = topInfoTextColor
    const fontSize = this.scaleFunc(14)
    ctx.font = `bold ${fontSize}px 'PingFangSC-Regular, PingFang SC'`
    /** 起始 y */
    let y = startY - this.scaleFunc(topInfoMargin)
    const topInfoList = this.getTopInfo(ctx)
    /** 是否有余料名称 */
    const hasSurplusStoreName = topInfoList.some(
      (i) => i.type === BoxType['topInfo:surplusStoreName']
    )
    // 如果有余料库名称，则将余料标识放在余料库名称前面
    if (hasSurplusStoreName) {
      this.setSize(this.scaleFunc(1220), this.scaleFunc(2440))
      const beforItem = JSON.parse(
        JSON.stringify(topInfoList.find((i) => i.before))
      )
      topInfoList.forEach((i) => (i.before = undefined))
      topInfoList[0].before = beforItem?.before
      topInfoList.push(topInfoList[0])
      topInfoList.shift()
    }
    topInfoList.forEach((item) => {
      const maxLineHeight = Math.max(
        item.lineHeight,
        item.after?.lineHeight ?? 0,
        item.before?.lineHeight ?? 0
      )
      /** 起始 x 每一行都是重新计算 */
      let x = startX
      // 绘制的起始位置
      const newY = y - this.scaleFunc(maxLineHeight)
      const centerY = (y + newY) / 2
      y = newY - this.scaleFunc(topInfoLineGap)
      // 核心文字之前的绘制
      if (item.before) {
        // 如果绘制类型是余料信息 如果后续有其他的则建议抽离成对应的方法以及采用 switch，目前只有一个则不进行复杂的处理
        if (item.before.type === BoxType['topInfo:surplusCircle']) {
          ctx.strokeStyle = '#000'
          ctx.lineWidth = 2
          const radius = this.scaleFunc(item.before.lineHeight)
          // 因为是中心点需要加上半径
          x += radius
          if (item.type === BoxType['topInfo:surplusStoreName']) {
            x += (radius * 2) / 3 // 为了保证与下面的尺寸标记对齐
          }
          drawCircleTextByMiddle(
            ctx,
            item.before?.text ?? '',
            x,
            centerY,
            radius
          )
          // 嗯就是这个意思   哪个意思？
          x += radius + (item.before.gap ?? 0)

          this.needMarkRectArr.push({
            x: x - radius - (item.before.gap ?? 0),
            y: centerY - radius,
            width: radius * 2,
            height: radius * 2,
            type: 'surplus',
            bigPlank: this.bigPlank,
          })
        }
      }
      // 如果是余料库名称，需要加上6的间距，为什么是 6 呢？为了对齐
      if (item.type === BoxType['topInfo:surplusStoreName']) {
        x += 6
      }
      ctx.fillText(item.text, x, centerY)
      // 核心文字之后的绘制
      // 目前没有就不写了如果有 x += measureText(text).width 然后照常绘制即可
    })
    ctx.restore()
  }
  /** @description 获取顶部要绘制的信息 */
  getTopInfo(ctx: CanvasRenderingContext2D) {
    const {
      canvasIndex,
      usedRate,
      plankHeight,
      plankWidth,
      surplusInfo,
      margin,
      isUseSaw,
    } = this.bigPlank
    let { width } = this.getMarginSize()
    if (checkBigPlankIsSpecialShape(this.bigPlank)) {
      const surWidth = surplusInfo?.width ?? 1220
      // TODO wc 待验证的大板款到处理
      // const surHeight = surplusInfo?.height ?? 2440
      // this.setSize(
      //   this.scaleFunc(surWidth > 1220 ? surWidth : 1220),
      //   this.scaleFunc(surHeight > 2440 ? surHeight : 2440)
      // )
      width = this.scaleFunc(surWidth > 1220 ? surWidth : 1220)
    }
    const { topInfoHeight, topInfoSurplusCircleR } = DrawBigPlankParams
    /** 板件序号信息 */
    const index = `【${canvasIndex}】`
    /** 优化率 */
    const rate = `${((usedRate > 1 ? 1 : usedRate) * 100).toFixed(2)}%-`
    const plankMargin =
      surplusInfo && surplusInfo.is_plus
        ? isUseSaw
          ? margin
          : store.state.selectStandardPlank.plankEdgeOff
        : margin
    /** 大板修边 */
    const bigpartMargin = plankMargin?.toString().includes(',')
      ? (plankMargin as string).split(',')[0]
      : plankMargin
    /** 板件尺寸信息 */
    let size = `${toDecimal(plankWidth, 2)}X${toDecimal(
      plankHeight,
      2
    )}-${bigpartMargin}`
    const { shape, isNotSurplus, branch_name } = surplusInfo ?? {}
    /** 是否是余料 */
    const isSurplus = surplusInfo && !isNotSurplus
    const isLSurplus =
      shape === 'lshape' ||
      (isSurplus &&
        surplusInfo?.path &&
        [6, 7].includes(surplusInfo?.path.length))
    /** L 型余料板件尺寸信息显示样式不同 */
    if (surplusInfo && isLSurplus) {
      const {
        longestHorizontalEdge,
        secondLongestHorizontalEdge,
        longestVerticalEdge,
        secondLongestVerticalEdge,
      } = getEdgeLengthsFromLShape(this.bigPlank.path ?? [])
      size = `[${toDecimal(longestHorizontalEdge.size, 2)}(${toDecimal(
        secondLongestHorizontalEdge.size,
        2
      )})X${toDecimal(longestVerticalEdge.size, 2)}(${toDecimal(
        secondLongestVerticalEdge.size,
        2
      )})]-${bigpartMargin}`
    }
    /** 是否需要换行显示优化率后面的尺寸信息，当[余]+序号+优化率+' '+尺寸在一行时宽度超过大板绘制宽度则将尺寸信息单独绘制一行 */
    const isLineBreak =
      ctx.measureText(`${index}${rate} ${size}`).width +
        // 余料的情况下需要考虑上余料本身绘制的圆圈宽度
        (isSurplus ? topInfoSurplusCircleR * 2 : 0) >
      width
    /** 余料库名称 */
    let surplusStoreName = branch_name === '-' ? '' : branch_name ?? ''
    if (surplusStoreName) {
      let addEllipsis = false
      /** 余料库名称超过绘制板宽则需要用省略号代替超出部分 */
      while (ctx.measureText(surplusStoreName).width >= width) {
        surplusStoreName = surplusStoreName.slice(0, -1)
        addEllipsis = true
      }
      if (addEllipsis) {
        surplusStoreName = surplusStoreName + '...'
      }
    }
    // 最终结果受顺序影响
    const result: TopInfoSingle[] = [
      {
        before: isSurplus
          ? {
              text: '余',
              type: BoxType['topInfo:surplusCircle'],
              lineHeight: topInfoSurplusCircleR as number,
            }
          : undefined,
        text: `${index}${rate} ${isLineBreak ? '' : size}`,
        lineHeight: topInfoHeight as number,
      },
    ]
    if (isLineBreak) {
      result.unshift({
        text: size,
        lineHeight: topInfoHeight as number,
      })
    }
    if (surplusStoreName) {
      result.unshift({
        text: `${surplusStoreName}`,
        lineHeight: topInfoHeight as number,
        type: BoxType['topInfo:surplusStoreName'],
      })
    }
    return result
  }
  /** @description 绘制大板切换正反以及单面板 */
  drawToggle(ctx: CanvasRenderingContext2D) {
    // 设置字体大小
    const fontSize = this.scaleFunc(14)
    ctx.font = `bold ${fontSize}px 'PingFangSC-Regular, PingFang SC' `
    const { ableToggle, sideType, noEngraving } = this.bigPlank
    const { x: startX, y: startY } = this.getMarginStart()
    // 宽度需要使用边框的宽度
    const { width: marginWidth, lineWidth } = this.getMarginSize()
    // 需要减去边框的宽度才能是此处需要的真正的宽度
    const width = marginWidth - (lineWidth as number) * 2
    const { toggleHeight } = DrawBigPlankParams
    /** 头部高度 */
    const scaleToggleHeight = this.scaleFunc(toggleHeight)
    /** 头部文字 y 值魔法数字 */
    const toggleTextY = this.scaleFunc(22)
    /** 圆圈 padding */
    const circlePadding = this.scaleFunc(5)
    // 需要绘制的内容
    interface DrawType {
      text: string
      x: number
      y: number
      width: number
      bgColor: string
      color: string
      after?: Omit<DrawType, 'x' | 'y' | 'width'>
      before?: Omit<DrawType, 'x' | 'y' | 'width'>
      margin?: number
      isNeedCircle?: boolean
    }
    const draws: DrawType[] = []

    if (!ableToggle) {
      // 单面板
      const singlePlankDrawObj: DrawType = {
        text: t('common.singlePlank'),
        x: startX,
        y: startY,
        width,
        bgColor: 'rgba(255, 255, 255, 0.9)',
        color: 'rgba(0, 0, 0, 0.6)',
      }

      draws.push(
        // 单面板的底色
        {
          text: '',
          x: startX,
          y: startY,
          width,
          bgColor: '#cfcfcf',
          color: '#fff',
        }
      )
      if (noEngraving) {
        Object.assign(singlePlankDrawObj, {
          after: {
            text: '雕',
            x: startX + width / 2 + 12,
            y: startY,
            width: width / 10,
            color: 'rgba(247, 158, 19)',
            bgColor: 'rgba(255, 255, 255, 0.9)',
            isNeedCircle: true,
            margin: 8,
          },
        })
        // 需要标记的矩形框传入
        this.needMarkRectArr.push({
          x: startX + width / 2 + 20,
          y: startY,
          width: width / 10 + circlePadding,
          height: width / 10 + circlePadding,
          type: 'singleMark',
          bigPlank: this.bigPlank,
        })
      }
      draws.push(singlePlankDrawObj)
    } else {
      const activeBgColor = '#18a8c7'
      const unActiveBgColor = '#fff'
      const activeColor = '#fff'
      const unActiveColor = 'rgba(0, 0, 0, 0.6)'
      const drawObjFront = {
        text: t('common.front'),
        x: startX,
        y: startY,
        width: width / 2,
        bgColor: sideType === 1 ? activeBgColor : unActiveBgColor,
        color: sideType === 1 ? activeColor : unActiveColor,
      }
      const drawObjBack = {
        text: t('common.back'),
        x: startX + width / 2,
        y: startY,
        width: width / 2,
        bgColor: sideType === -1 ? activeBgColor : unActiveBgColor,
        color: sideType === -1 ? activeColor : unActiveColor,
      }
      draws.push(drawObjFront, drawObjBack)
    }

    function drawFunc(draw: DrawType) {
      const {
        x,
        y,
        text,
        bgColor,
        color,
        width: itemWidth,
        isNeedCircle,
      } = draw
      const { width: textWidth } = ctx.measureText(text)
      // 绘制矩形框
      ctx.fillStyle = bgColor
      ctx.fillRect(x, y, itemWidth, scaleToggleHeight)
      // 绘制文字
      ctx.fillStyle = color
      ctx.fillText(
        text,
        x + (itemWidth / 2 - textWidth / 2),
        // 22是个魔法数字
        y + toggleTextY
      )
      if (isNeedCircle) {
        ctx.fillStyle = color
        ctx.strokeStyle = color
        /** 圆圈半径 */
        const radius = textWidth / 2 + circlePadding
        // 绘制圆圈
        ctx.beginPath()
        // 1 为魔法数字，就是要才能居中 你懂的
        ctx.arc(
          x + (itemWidth / 2 - textWidth / 2) + circlePadding + 1.5,
          y + toggleTextY / 2 + circlePadding + 1.5,
          radius,
          0,
          Math.PI * 2
        )
        ctx.stroke()
        ctx.closePath()
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
      }
    }

    draws.forEach((draw) => {
      drawFunc(draw)
      const { x, y, text, width: itemWidth, margin } = draw
      // 中心文字宽度
      const textWidth = ctx.measureText(text).width
      if (draw.after) {
        const { margin: afterMargin } = draw.after
        // 绘制文字宽度
        const afterTextWidth = ctx.measureText(draw.after.text).width
        const afterStartX =
          x + itemWidth / 2 + textWidth / 2 + this.scaleFunc(margin ?? 0)
        drawFunc({
          ...draw.after,
          x: afterStartX + (afterMargin ?? 0),
          y: y,
          width: afterTextWidth,
        })
      }
      if (draw.before) {
        const { margin: beforeMargin } = draw.before
        // 绘制文字宽度
        const beforeTextWidth = ctx.measureText(draw.before.text).width
        const beforeStartX =
          x +
          itemWidth / 2 -
          textWidth / 2 -
          beforeTextWidth -
          this.scaleFunc(margin ?? 0)
        drawFunc({
          ...draw.before,
          x: beforeStartX - (beforeMargin ?? 0),
          y: y,
          width: beforeTextWidth,
        })
      }
    })
  }
  /** @description 绘制大板边框 */
  drawMargin(ctx: CanvasRenderingContext2D) {
    const {
      normalMarginStroke,
      activeMarginStroke,
      normalMarginStrokeColor,
      activeMarginStrokeColor,
    } = DrawBigPlankParams
    const lineWidth = this.isActive ? activeMarginStroke : normalMarginStroke
    const strokeStyle = this.isActive
      ? activeMarginStrokeColor
      : normalMarginStrokeColor
    ctx.lineWidth = lineWidth
    ctx.strokeStyle = strokeStyle
    const { x, y, width, height } = this.getMarginSize()
    ctx.strokeRect(x, y, width, height)
    return
  }
  /** @description 获取边框大小 */
  getMarginSize() {
    const { normalMarginStroke, activeMarginStroke, margin, toggleHeight } =
      DrawBigPlankParams
    const lineWidth = this.isActive ? activeMarginStroke : normalMarginStroke
    const { width, height } = this.getSize()
    const { x, y } = this.getMarginStart()
    return {
      x: x - lineWidth,
      y: y - lineWidth,
      width: width + 2 * this.scaleFunc(margin) + 2 * lineWidth,
      height:
        height +
        this.scaleFunc(toggleHeight) +
        2 * this.scaleFunc(margin) +
        2 * lineWidth,
      lineWidth,
    }
  }
  /**
   * 绘制四边修边
   * @param ctx
   */
  drawFourEdge(ctx: CanvasRenderingContext2D) {
    const { x, y, width, height } = this.getMarginSize()
    const isUseSaw = this.bigPlank.isUseSaw

    const surplusInfo = this.bigPlank.surplusInfo
    const isPlus = surplusInfo?.is_plus
    let plankMargin = String(this.bigPlank.margin)
    if (surplusInfo && isPlus) {
      plankMargin = String(
        isUseSaw
          ? this.bigPlank.margin
          : store.state.selectStandardPlank.plankEdgeOff
      )
    }

    if (plankMargin?.includes(',')) {
      ctx.save()
      ctx.fillStyle = '#000'
      const marginArr = (plankMargin as string).split(',')
      // 左修边
      ctx.fillText(
        marginArr[2],
        x - (ctx.measureText(marginArr[2]).width as number) - 5,
        y + height / 2
      )
      // 下修边
      ctx.fillText(marginArr[1], x + width / 2 - 3, y + height + 17)
      // 右修边
      ctx.fillText(marginArr[3], x + width + 4, y + height / 2)
      ctx.restore()
    }
  }
  /** @description 绘制大板真正所占据的大小 */
  drawContainer(
    ctx: CanvasRenderingContext2D,
    options: {
      trimSide: string
      plankBgColor?: string
    }
  ) {
    const { margin, toggleHeight, plankBgColor } = DrawBigPlankParams
    const { x: startX, y: startY } = this.getMarginStart()
    const x = startX + this.scaleFunc(margin)
    const y = startY + this.scaleFunc(toggleHeight) + this.scaleFunc(margin)
    ctx.fillStyle = options.plankBgColor || plankBgColor
    const pathArr = genDrawBigPlankPath(
      this.bigPlank,
      this.ncSetting,
      { defaultScale: this.defaultScale, scale: this.scale, ...options },
      x,
      y
    )
    // const pathArr = this.bigPlank.path || []
    ctx.beginPath()
    pathArr.forEach(({ x, y }, idx) => {
      if (idx === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    })
    ctx.fill()
    ctx.closePath()
  }

  drawOverSizeArea(
    ctx: CanvasRenderingContext2D,
    options: {
      trimSide: string
      plankBgColor?: string
    }
  ) {
    if (!this.ncSetting.counter_limit_able) return
    const { margin, toggleHeight } = DrawBigPlankParams
    const { x: startX, y: startY } = this.getMarginStart()
    const x = startX + this.scaleFunc(margin)
    const y = startY + this.scaleFunc(toggleHeight) + this.scaleFunc(margin)
    const plankWidth = this.bigPlank.plankWidth
    const plankHeight = this.bigPlank.plankHeight
    // 计算分隔的 x/y 坐标（台面宽度对应坐标）
    const scaleFunc = translateScaleByDraw(this.defaultScale, this.scale)
    const counter_width = this.ncSetting.counter_width ?? 500
    const limitSize = this.ncSetting.xyReverse ? plankHeight : plankWidth
    if (counter_width && counter_width < limitSize) {
      const pathArr = genDrawBigPlankPath(
        this.bigPlank,
        this.ncSetting,
        { defaultScale: this.defaultScale, scale: this.scale, ...options },
        x,
        y
      )

      /** 工位起始点 */
      const startPoint = this.ncSetting.startPosition

      // 工位有效区方向，为true时，台面在右侧，台面外在左侧
      const isRight = startPoint.includes('右')

      const minX = Math.min(...pathArr.map((p) => p.x))
      const maxX = Math.max(...pathArr.map((p) => p.x))
      const minY = Math.min(...pathArr.map((p) => p.y))
      const maxY = Math.max(...pathArr.map((p) => p.y))
      const drawPlankWidth = maxX - minX
      const drawPlankHeight = maxY - minY

      // 注意：必须加括号，否则 `+` 优先于 `?:`，会变成 (x + isRight) ? A : B
      const splitX = isRight
        ? x + drawPlankWidth - scaleFunc(counter_width)
        : x + scaleFunc(counter_width)

      // 工位有效区在下方：左下角/右下角，为true时，台面在下方，台面外在上方
      const isBottom = ['左下角', '右下角'].includes(startPoint)

      const splitY = isBottom
        ? y + drawPlankHeight - scaleFunc(counter_width)
        : y + scaleFunc(counter_width)

      // 使用 Clipper 获取台面外闭合路径 方向取决于工位有效区在下方还是上方 由于这里取台面外，所以需要取反
      const rightPath = this.ncSetting.xyReverse
        ? getPathBySplitYWithClipper(pathArr, splitY, !isBottom)
        : getPathBySplitXWithClipper(pathArr, splitX, !isRight)

      if (rightPath.length > 0) {
        ctx.save()
        ctx.fillStyle = '#e698a3'
        const fontSize = this.scaleFunc(14)
        ctx.font = `bold ${fontSize}px 'PingFangSC-Regular, PingFang SC'`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        const text = '台面外'
        const charWidth = ctx.measureText(text[0]).width
        const textWidth = ctx.measureText(text).width
        const charGap = this.scaleFunc(2)
        rightPath.forEach((path) => {
          ctx.beginPath()
          path.forEach(({ x: px, y: py }, idx) => {
            if (idx === 0) {
              ctx.moveTo(px, py)
            } else {
              ctx.lineTo(px, py)
            }
          })
          ctx.fill()
          ctx.closePath()
          // 路径内最大矩形中心绘制「台面外」
          drawOverSizeAreaLabel(ctx, path, {
            text,
            fontSize,
            charWidth,
            textWidth,
            charGap,
          })
        })
        ctx.restore()
      }
    }
  }

  /** @description 绘制大板锁定标识 */
  drawLock(ctx: CanvasRenderingContext2D): DrawBoxReturnType {
    const { isLocked } = this.bigPlank
    const { x, y } = this.getContainerStart()
    const { width } = this.getSize()
    const size = this.scaleFunc(24)
    const startX = x + width + this.scaleFunc(15)
    const startY = y - this.scaleFunc(30)
    // 无论哪种情况都讲绘制的位置抛出
    // 激活或者已锁定的情况都需要显示
    const result: DrawBoxReturnType = {
      type: BoxType['lock:lock'],
      path: rectToPath({ x: startX, y: startY, width: size, height: size }),
      plankStartX: x,
      plankStartY: y,
      plank: this.bigPlank,
    }
    if (!this.isActive && !isLocked) return result
    const img = isLocked ? this.images.unlock : this.images.lock
    if (img) {
      ctx.drawImage(img, startX, startY, size, size)
    }
    // 返回绘制位置
    return result
  }
  /** @description 绘制更多设置 */
  drawMoreSetting(ctx: CanvasRenderingContext2D) {
    if (!this.isActive) return
    ctx.fillStyle = '#ECECEC'
    const { x: startX, width } = this.getMarginSize()
    const { y: startY } = this.getContainerStart()
    const { margin } = DrawBigPlankParams
    const x = startX + width + this.scaleFunc(margin) + this.scaleFunc(2)
    const y = startY
    const long = this.scaleFunc(40)
    const r = this.scaleFunc(2.5)
    ctx.fillRect(x, y, long, long)
    const arcY = y + this.scaleFunc(20)
    let arcX = 0
    ctx.fillStyle = '#666'
    for (let index = 0; index < 3; index++) {
      ctx.beginPath()
      ctx.arc(x + this.scaleFunc((arcX += 10)), arcY, r, 0, Math.PI * 2, true)
      ctx.closePath()
      ctx.fill()
    }
  }
  /**
   * @description 绘制复用标记文字（X号大板复用）
   * @param ctx CanvasRenderingContext2D
   * @param parentCanvasIndex 父板序号
   */
  drawReuseLabel(ctx: CanvasRenderingContext2D, parentCanvasIndex: number) {
    if (!this.bigPlank._reuseParentId) return
    const { x: startX, width } = this.getMarginSize()
    const { y: startY } = this.getContainerStart()
    const { margin } = DrawBigPlankParams
    const threeDotX =
      startX + width + this.scaleFunc(margin) + this.scaleFunc(2)
    const threeDotY = startY
    const threeDotSize = this.scaleFunc(15)
    const fontSize = this.scaleFunc(13)
    const charGap = this.scaleFunc(2)
    const text = translateLang('{number}号大板复用', {
      number: parentCanvasIndex,
    })
    ctx.save()
    ctx.fillStyle = '#18a8c7'
    ctx.font = `${fontSize}px 'PingFangSC-Regular, PingFang SC'`
    const centerX = threeDotX + threeDotSize / 2
    const bottomMargin = this.scaleFunc(15)
    const textStartY = threeDotY + this.scaleFunc(40) + bottomMargin
    // 绘制竖向文字（从上往下绘制）
    ctx.textBaseline = 'top'
    for (let i = 0; i < text.length; i++) {
      const charWidth = ctx.measureText(text[i]).width
      // 水平居中对齐每个字符
      const charX = centerX - charWidth / 2
      const charY = textStartY + i * (fontSize + charGap)
      ctx.fillText(text[i], charX, charY)
    }
    ctx.restore()
  }
  /** @description 设置活跃状态 */
  setActive(isActive: boolean) {
    this.isActive = isActive
  }
  /** @description 根据缩放比例缩放 */
  scaleFunc(num: number) {
    return num * this.scale
  }
}

/**
 * @description 返回大板绘制路径(余料以及标准大板)(目前这种写法还是很死基本属于硬编码，后续有时间需要修改成固有路径加旋转反面操作来得到其想要的缺口位置比较好)
 * @param bigPlank 大板信息
 * @param ncSetting nc 设置信息
 * @param options { defaultScale: number; scale: number } 默认缩放/动态缩放
 * @param startX 起始 x
 * @param startY 起始 y
 * @returns
 */
export function genDrawBigPlankPath(
  bigPlank: IBigPlank,
  ncSetting: any,
  options: {
    defaultScale: number
    scale: number
    trimSide: string
  } = {
    defaultScale: 1,
    scale: 1,
    trimSide: '',
  },
  startX = 0,
  startY = 0
) {
  const { defaultScale, scale } = options
  /** 缩放函数，合其他地方的绘制函数相同 */
  const scaleFunc = translateScaleByDraw(defaultScale, scale)
  return (bigPlank.path || []).map((pt) => ({
    x: startX + scaleFunc(pt.x),
    y: startY + scaleFunc(pt.y),
  }))
}

/**
 * @description 返回一个函数用于计算当前值根据指定缩放值缩放后的大小
 */
function translateScaleByDraw(defaultScale: number, scale: number) {
  return (size: number) => (size / defaultScale) * scale
}

/**
 * @description 取多边形在指定 y 上的水平内截面（偶数交点成对）
 */
function getHorizontalSpansInPolygon(
  path: PointType[],
  y: number
): Array<{ x1: number; x2: number }> {
  if (!path || path.length < 3) return []
  const xs: number[] = []
  const n = path.length
  for (let i = 0; i < n; i++) {
    const a = path[i]
    const b = path[(i + 1) % n]
    if (Math.abs(a.y - b.y) < 1e-6) continue
    const ymin = Math.min(a.y, b.y)
    const ymax = Math.max(a.y, b.y)
    // 半开区间，避免顶点被重复计入
    if (y < ymin || y >= ymax) continue
    const t = (y - a.y) / (b.y - a.y)
    xs.push(a.x + t * (b.x - a.x))
  }
  xs.sort((a, b) => a - b)
  const spans: Array<{ x1: number; x2: number }> = []
  for (let i = 0; i + 1 < xs.length; i += 2) {
    spans.push({ x1: xs[i], x2: xs[i + 1] })
  }
  return spans
}

/**
 * @description 两组水平截面求交集
 */
function intersectSpanLists(
  a: Array<{ x1: number; x2: number }>,
  b: Array<{ x1: number; x2: number }>
): Array<{ x1: number; x2: number }> {
  const result: Array<{ x1: number; x2: number }> = []
  a.forEach((sa) => {
    b.forEach((sb) => {
      const x1 = Math.max(sa.x1, sb.x1)
      const x2 = Math.min(sa.x2, sb.x2)
      if (x2 > x1) result.push({ x1, x2 })
    })
  })
  return result
}

type MaxRectInPath = {
  x: number
  y: number
  width: number
  height: number
}

/**
 * @description 求多边形内面积最大的轴对齐矩形（扫描线近似）
 */
function getMaxRectInPolygon(path: PointType[]): MaxRectInPath | null {
  if (!path || path.length < 3) return null
  let minY = Infinity
  let maxY = -Infinity
  path.forEach(({ y }) => {
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  })
  const height = maxY - minY
  if (height <= 0) return null

  // 扫描线采样；略微内缩避免贴边采样落在边界外
  const inset = Math.min(height * 0.01, 1)
  const sampleMinY = minY + inset
  const sampleMaxY = maxY - inset
  if (sampleMaxY <= sampleMinY) return null

  const samples = 36
  const scanYs: number[] = []
  const spansAtY: Array<Array<{ x1: number; x2: number }>> = []
  for (let i = 0; i <= samples; i++) {
    const y = sampleMinY + ((sampleMaxY - sampleMinY) * i) / samples
    const spans = getHorizontalSpansInPolygon(path, y)
    if (!spans.length) continue
    scanYs.push(y)
    spansAtY.push(spans)
  }
  if (scanYs.length < 2) return null

  let best: (MaxRectInPath & { area: number }) | null = null
  for (let top = 0; top < scanYs.length; top++) {
    let commonSpans = spansAtY[top]
    for (let bottom = top; bottom < scanYs.length; bottom++) {
      if (bottom > top) {
        commonSpans = intersectSpanLists(commonSpans, spansAtY[bottom])
        if (!commonSpans.length) break
      }
      const rectHeight = scanYs[bottom] - scanYs[top]
      if (rectHeight <= 0) continue
      commonSpans.forEach(({ x1, x2 }) => {
        const rectWidth = x2 - x1
        if (rectWidth <= 0) return
        const area = rectWidth * rectHeight
        if (!best || area > best.area) {
          best = {
            x: x1,
            y: scanYs[top],
            width: rectWidth,
            height: rectHeight,
            area,
          } as MaxRectInPath & { area: number }
        }
      })
    }
  }
  if (best) {
    return best
  } else {
    return null
  }
}

/**
 * @description 在台面外路径内最大矩形中心绘制「台面外」文字
 * 横向/纵向规则：矩形宽度 >= 整词宽度横排，否则竖排；宽度小于单字则不绘制
 */
export function drawOverSizeAreaLabel(
  ctx: CanvasRenderingContext2D,
  path: PointType[],
  options: {
    text: string
    fontSize: number
    charWidth: number
    textWidth: number
    charGap: number
  }
) {
  if (!path?.length) return
  const { text, fontSize, charWidth, textWidth, charGap } = options
  const maxRect = getMaxRectInPolygon(path)
  if (!maxRect) return

  const { width: areaWidth, height: areaHeight } = maxRect
  // 宽度小于单个文字宽度则不绘制
  if (areaWidth < charWidth) return

  const cx = maxRect.x + areaWidth / 2
  const cy = maxRect.y + areaHeight / 2

  ctx.save()
  ctx.beginPath()
  path.forEach(({ x: px, y: py }, idx) => {
    if (idx === 0) {
      ctx.moveTo(px, py)
    } else {
      ctx.lineTo(px, py)
    }
  })
  ctx.closePath()
  ctx.clip()

  ctx.fillStyle = '#fff'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  if (areaWidth >= textWidth) {
    // 宽度够：横着展示
    ctx.fillText(text, cx, cy)
  } else {
    // 宽度不够：竖着展示
    const totalHeight = text.length * fontSize + (text.length - 1) * charGap
    let charY = cy - totalHeight / 2 + fontSize / 2
    for (let i = 0; i < text.length; i++) {
      ctx.fillText(text[i], cx, charY)
      charY += fontSize + charGap
    }
  }
  ctx.restore()
}

/**
 * @description 使用 Clipper 根据给定的 x 值分隔路径为左右两侧
 * @param pathArr 路径点数组
 * @param splitX 分隔的 x 坐标值
 * @param isRight true 取右侧（x >= splitX），false 取左侧
 * @returns 裁切后的闭合路径数组
 */
export function getPathBySplitXWithClipper(
  pathArr: PointType[],
  splitX: number,
  isRight = true
): PointType[][] {
  if (!pathArr || pathArr.length === 0) {
    return []
  }

  const yValues = pathArr.map((p) => p.y)
  const minY = Math.min(...yValues)
  const maxY = Math.max(...yValues)
  const maxX = Math.max(...pathArr.map((p) => p.x))
  const minX = Math.min(...pathArr.map((p) => p.x))

  // 右侧/左侧矩形裁剪区域（覆盖整个 y 范围并略微外扩）
  const clipRect: PointUpperCase[] = isRight
    ? [
        { X: splitX, Y: minY - 1000 },
        { X: maxX + 1000, Y: minY - 1000 },
        { X: maxX + 1000, Y: maxY + 1000 },
        { X: splitX, Y: maxY + 1000 },
      ]
    : [
        { X: minX - 1000, Y: minY - 1000 },
        { X: splitX, Y: minY - 1000 },
        { X: splitX, Y: maxY + 1000 },
        { X: minX - 1000, Y: maxY + 1000 },
      ]

  return intersectPathWithClipRect(pathArr, clipRect)
}

/**
 * @description 使用 Clipper 根据给定的 y 值分隔路径为上下两侧
 * @param pathArr 路径点数组
 * @param splitY 分隔的 y 坐标值
 * @param isBottom true 取下侧（y >= splitY），false 取上侧
 * @returns 裁切后的闭合路径数组
 */
export function getPathBySplitYWithClipper(
  pathArr: PointType[],
  splitY: number,
  isBottom = true
): PointType[][] {
  if (!pathArr || pathArr.length === 0) {
    return []
  }

  const yValues = pathArr.map((p) => p.y)
  const minY = Math.min(...yValues)
  const maxY = Math.max(...yValues)
  const maxX = Math.max(...pathArr.map((p) => p.x))
  const minX = Math.min(...pathArr.map((p) => p.x))

  // 下侧/上侧矩形裁剪区域（覆盖整个 x 范围并略微外扩）
  const clipRect: PointUpperCase[] = isBottom
    ? [
        { X: minX - 1000, Y: splitY },
        { X: maxX + 1000, Y: splitY },
        { X: maxX + 1000, Y: maxY + 1000 },
        { X: minX - 1000, Y: maxY + 1000 },
      ]
    : [
        { X: minX - 1000, Y: minY - 1000 },
        { X: maxX + 1000, Y: minY - 1000 },
        { X: maxX + 1000, Y: splitY },
        { X: minX - 1000, Y: splitY },
      ]

  return intersectPathWithClipRect(pathArr, clipRect)
}

/**
 * @description 用 Clipper 取路径与裁剪矩形的交集，并还原为点位路径
 */
function intersectPathWithClipRect(
  pathArr: PointType[],
  clipRect: PointUpperCase[]
): PointType[][] {
  const SCALE = 10000
  const { Clipper, ClipType, PolyType, PolyFillType, Paths, JS } =
    window.ClipperLib

  const clipperPath = toUpperCasePoint(pathArr) as PointUpperCase[]
  const scaledPath = window.$copy([clipperPath])
  const scaledClipRect = window.$copy([clipRect])
  JS.ScaleUpPaths(scaledPath, SCALE)
  JS.ScaleUpPaths(scaledClipRect, SCALE)

  const clipper = new Clipper()
  clipper.AddPaths(scaledPath, PolyType.ptSubject, true)
  clipper.AddPaths(scaledClipRect, PolyType.ptClip, true)

  const solution = new Paths()
  clipper.Execute(
    ClipType.ctIntersection,
    solution,
    PolyFillType.pftNonZero,
    PolyFillType.pftNonZero
  )

  if (solution.length === 0) {
    return []
  }

  const resPath = JSON.parse(JSON.stringify(solution))
  const results: PointType[][] = []
  if (resPath.length) {
    for (const path of resPath) {
      const result: PointType[] = path.map((v: PointUpperCase) => ({
        x: Number((v.X / SCALE).toFixed(3)),
        y: Number((v.Y / SCALE).toFixed(3)),
      }))
      results.push(result)
    }
  }
  return results
}

/**
 * @description 绘制圆圈带文字，基于文字基线是 middle
 * @param ctx
 * @param text
 * @param x
 * @param y
 * @param r
 * @param scale
 */
function drawCircleTextByMiddle(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  r: number
) {
  ctx.beginPath()
  ctx.arc(x, y, r, Math.PI * 2, 0)
  ctx.closePath()
  ctx.stroke()
  const { width } = ctx.measureText(text)
  ctx.fillText(text, x - width / 2, y)
}

/**
 * @description 初始化绘制数据，生成每一个大板初始所在位置，整个 canvas 初始高度
 */
// 测试数据 canvas默认宽度
export function initBigPlanksPositionToCanvas(
  bigPlanks: IBigPlank[],
  scale: number,
  dWidth: number
) {
  const scaleFunc = translateScaleByDraw(6, scale)
  const {
    plankMarginY,
    plankMarginX,
    plankMarginLeft,
    plankMarginTop,
    margin,
    canvasExtraMargin,
  } = DrawBigPlankParams
  /** 记录每行行的最高行高 */
  const lineMaxHeightArr: number[] = []
  let currentLine = 0
  /** 上一个处理过的大板(用于定位下一个的位置) */
  let lastPlank: IBigPlank | null = null
  const plankEdge = margin * scale
  /** 首个大板 y */
  const firstLeft = plankMarginLeft * scale
  /** 首个大板 x */
  const firstTop = plankMarginTop * scale
  /** 两个大板纵向间隔 */
  const verticalGap = plankMarginY * scale
  /** 两个大板横向间隔 */
  const horizontalGap = plankMarginX * scale
  bigPlanks.forEach((plank, idx) => {
    const { surplusInfo, plankHeight, plankWidth } = plank
    // 生成绘制所用的宽高
    const [minX, minY, maxX, maxY] = getPathMinMaxPoint(plank.path ?? [])
    plank.width = scaleFunc(maxX - minX)
    plank.height = scaleFunc(maxY - minY)
    // if (isSurplus(plank)) {
    //   plank.height = scaleFunc(surplusInfo?.height ?? 0)
    //   plank.width = scaleFunc(surplusInfo?.width ?? 0)
    // } else {
    //   plank.height = scaleFunc(plankHeight ?? 0)
    //   plank.width = scaleFunc(plankWidth ?? 0)
    // }
    // 记录每一行的最高高度
    lineMaxHeightArr[currentLine] = Math.max(
      lineMaxHeightArr[currentLine] ?? 0,
      // 需要加一个纵向的间距因为其起始位置是大板边框上plankMarginY / 2
      plank.height + verticalGap
    )
    if (idx === 0) {
      // 第一个生成初始位置
      plank.startX = firstLeft
      plank.startY = firstTop
    } else {
      const lPlank = lastPlank!
      const { startX: lStartX, startY: lStartY } = lPlank
      let lWidth = lPlank.width
      if (checkBigPlankIsSpecialShape(lPlank)) {
        lWidth = lPlank.width > scaleFunc(1220) ? lPlank.width : scaleFunc(1220)
      }

      // 如果当前大板超出一行，则换行
      if (lStartX + lWidth + plankEdge + plank.width + horizontalGap > dWidth) {
        plank.startX = firstLeft
        // 最后一张大板的底部+大板和边框边距+两个大板间距
        plank.startY =
          lStartY +
          plankEdge +
          lineMaxHeightArr[currentLine] +
          // 说明一下为什么(好想贴张图，为什么不能贴图)：因为绘制的起点是上面说的那个逻辑，所以需要知道下一个板件的绘制起点就需要在加上一半的边距
          verticalGap +
          verticalGap / 2
        // 行数+1
        currentLine++
        lineMaxHeightArr[currentLine] = plank.height + verticalGap
      } else {
        // 如果还在当前行
        plank.startX = lStartX + lWidth + horizontalGap
        plank.startY = lStartY
      }
    }

    lastPlank = plank
  })
  // 返回总高
  return (
    ((lastPlank as any)?.startY ?? 0) +
    (Math.max(...lineMaxHeightArr) ?? 0) +
    firstTop +
    canvasExtraMargin
  )
}
